import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faRotateRight, faSearch, faInfoCircle, faPaperPlane, faClipboardCheck, faPenToSquare, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import Select, { StylesConfig } from 'react-select';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import DeviceChecklistService from 'api/deviceChecklist/DeviceChecklistService';
import { DeviceChecklistDto } from 'api/deviceChecklist/models/DeviceChecklistDto';
import { TrackingItemDto } from 'api/deviceChecklist/models/TrackingItemDto';
import { Paged } from 'api/common/types/Page';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import { FotaConfigFileDto } from 'api/deviceChecklist/models/FotaConfigFileDto';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import UsersService from 'api/users/UsersService';
import VehiclesService from 'api/vehicles/VehiclesService';
import CompaniesService from 'api/companies/CompaniesService';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import TrackingItemsTable from 'common/components/trackingItemsTable/TrackingItemsTable';
import FotaApplyModal from './FotaApplyModal';

interface PlateOption {
    value: string;
    label: string;
}

type CheckStatus = 'loading' | 'pass' | 'fail' | 'info';

interface CheckItem {
    key: string;
    status: CheckStatus;
    messageKey?: string;
    messageSuffix?: string;
    messageRaw?: string;
    lastTagCode?: string;
    lastTagDate?: string;
    currentConfigFile?: string;
    latestConfigFile?: string;
    requiresFotaAuth?: boolean;
    configUpdateDate?: string;
    fotaBlocked?: boolean;
}

const STEP_DELAY = 800;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const Spinner: React.FC<{ className?: string }> = ({ className = '' }) => (
    <svg className={`animate-spin h-4 w-4 text-current ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

const DeviceChecklistScreen: React.FC = () => {
    const { t } = useTranslation();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const isPlatformAdmin = UsersService.isPlatformAdmin(userProfile?.roles);
    const isFotaAdmin = UsersService.isAdmin(userProfile?.roles) || isPlatformAdmin;
    const [licensePlate, setLicensePlate] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [searching, setSearching] = useState(false);
    const [data, setData] = useState<DeviceChecklistDto | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [checks, setChecks] = useState<CheckItem[]>([]);
    const [runningChecks, setRunningChecks] = useState(false);
    const [kmValue, setKmValue] = useState('');
    const [fuelTankValue, setFuelTankValue] = useState('');
    const [sendingOdoset, setSendingOdoset] = useState(false);
    const [savingFuelTank, setSavingFuelTank] = useState(false);
    const [voltageValue, setVoltageValue] = useState('13100');
    const [voltageCustom, setVoltageCustom] = useState(false);
    const [sendingVoltage, setSendingVoltage] = useState(false);
    const [voltageParamLoading, setVoltageParamLoading] = useState(false);
    const [voltageParamLoaded, setVoltageParamLoaded] = useState<boolean | null>(null); // null=not attempted, true=loaded, false=failed
    const [voltageParamCountdown, setVoltageParamCountdown] = useState(0);
    const [vehicleOptions, setVehicleOptions] = useState<PlateOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsLoadError, setOptionsLoadError] = useState(false);
    const [fotaEditOpen, setFotaEditOpen] = useState(false);
    const [fotaConfigs, setFotaConfigs] = useState<FotaConfigFileDto[] | null>(null);
    const [fotaConfigsLoading, setFotaConfigsLoading] = useState(false);
    const [fotaConfigsError, setFotaConfigsError] = useState<string | null>(null);
    const [fotaSelectedFileId, setFotaSelectedFileId] = useState<number | null>(null);
    const [fotaApplying, setFotaApplying] = useState(false);
    const [fotaKmValue, setFotaKmValue] = useState('');
    const [fotaApplyModalOpen, setFotaApplyModalOpen] = useState(false);
    const [trackingItemsOpen, setTrackingItemsOpen] = useState(false);
    const [trackingPage, setTrackingPage] = useState<Paged<TrackingItemDto> | null>(null);
    const [trackingCurrentPage, setTrackingCurrentPage] = useState(1);
    const [trackingLoading, setTrackingLoading] = useState(false);

    const abortRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        setOptionsLoading(true);
        const vehiclesPromise = VehiclesService.getList({ page: 1, itemsPerPage: 1000 });
        const companiesPromise = isPlatformAdmin ? CompaniesService.getCompanies() : Promise.resolve([]);
        Promise.all([vehiclesPromise, companiesPromise])
            .then(([res, companies]) => {
                if (cancelled) return;
                const companyMap = new Map<string, string>();
                for (const c of companies) {
                    if (c.value && c.label) companyMap.set(c.value, c.label);
                }
                const seen = new Set<string>();
                const opts: PlateOption[] = [];
                for (const v of res.items ?? []) {
                    const plate = (v.licensePlate ?? '').toUpperCase().trim();
                    if (!plate || seen.has(plate)) continue;
                    seen.add(plate);
                    const designation = (v.designation ?? '').trim();
                    const companyName = isPlatformAdmin && v.companyId ? companyMap.get(v.companyId) : undefined;
                    let label = designation ? `${plate} — ${designation}` : plate;
                    if (companyName) {
                        label += ` (${companyName})`;
                    }
                    opts.push({ value: plate, label });
                }
                opts.sort((a, b) => a.value.localeCompare(b.value));
                setVehicleOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setOptionsLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setOptionsLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (trackingItemsOpen && data?.vehicleId) {
            loadTrackingItems(data.vehicleId, selectedDate, 1);
        }
    }, [selectedDate]);

    const isToday = (dateStr?: string): boolean => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        const now = new Date();
        return d.getFullYear() === now.getFullYear()
            && d.getMonth() === now.getMonth()
            && d.getDate() === now.getDate();
    };

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString();
    };

    const formatDateForApi = (d: Date): string => {
        return d.toISOString();
    };

    const buildCheckResult = useCallback((key: string, dto: DeviceChecklistDto): CheckItem => {
        switch (key) {
            case 'check_tracking_today':
                if (!dto.filteredTrackingDate) {
                    return { key, status: 'fail', messageKey: 'no_tracking_data' };
                }
                if (!isToday(dto.filteredTrackingDate)) {
                    return { key, status: 'info', messageKey: 'tracking_not_today', messageRaw: formatDate(dto.filteredTrackingDate) };
                }
                return { key, status: 'pass', messageRaw: formatDate(dto.filteredTrackingDate) };
            case 'check_gps_coordinates':
                if (dto.latitude !== 0 && dto.longitude !== 0) {
                    return { key, status: 'pass', messageRaw: `${dto.latitude}, ${dto.longitude}` };
                }
                return { key, status: 'fail', messageKey: 'no_gps_signal' };
            case 'check_km':
                if (dto.hasCanReading) {
                    if (dto.km > 0) {
                        return { key, status: 'pass', messageRaw: `${dto.km} km` };
                    }
                    return { key, status: 'fail', messageKey: 'no_km_data' };
                }
                if (dto.km > 100) {
                    return { key, status: 'pass', messageRaw: `${t('deviceChecklist.no_can_has_value', { km: dto.km })}` };
                }
                return { key, status: 'info', messageKey: 'has_can_reading_false_info', messageRaw: dto.km > 0 ? `${t('deviceChecklist.current_value')}: ${dto.km} km` : undefined };
            case 'check_fuel':
                if (dto.fuelTank > 0) {
                    return { key, status: 'pass', messageRaw: `${dto.fuelTank} L` };
                }
                if (!dto.hasCanReading) {
                    return { key, status: 'info', messageKey: 'fuel_tank_not_required' };
                }
                return { key, status: 'fail', messageKey: 'no_fuel_tank' };
            case 'check_identification_tag':
                if (!dto.supportsIdentificationTag) {
                    return { key, status: 'info', messageKey: 'identification_tag_not_supported' };
                }
                if (!dto.hasIdentificationTag) {
                    return { key, status: 'info', messageKey: 'identification_tag_not_required' };
                }
                if (dto.driverTag) {
                    return {
                        key,
                        status: 'pass',
                        messageKey: 'identification_tag_present',
                        messageSuffix: dto.driverTag,
                        lastTagCode: dto.lastIdentificationTagCode,
                        lastTagDate: dto.lastIdentificationTagDate,
                    };
                }
                return {
                    key,
                    status: 'fail',
                    messageKey: 'identification_tag_absent',
                    lastTagCode: dto.lastIdentificationTagCode,
                    lastTagDate: dto.lastIdentificationTagDate,
                };
            default:
                return { key, status: 'info' };
        }
    }, []);

    const getCheckKeys = useCallback((dto: DeviceChecklistDto): string[] => {
        const keys = [
            'check_device_configuration',
            'check_tracking_today',
            'check_gps_coordinates',
            'check_km',
        ];
        if (dto.hasCanReading) {
            keys.push('check_fuel');
        }
        if (dto.supportsIdentificationTag) {
            keys.push('check_identification_tag');
        }
        keys.push('check_voltage_battery');
        return keys;
    }, []);

    const runFotaConfigCheck = useCallback(async (dto: DeviceChecklistDto): Promise<CheckItem> => {
        const key = 'check_device_configuration';
        if (!dto.deviceCode) {
            return { key, status: 'info', messageKey: 'device_config_not_found', fotaBlocked: true };
        }
        try {
            const fota = await DeviceChecklistService.getFotaConfig(dto.deviceCode, dto.deviceModelName ?? '');
            if (fota.sessionExpired) {
                return { key, status: 'info', messageKey: 'device_config_auth_required', requiresFotaAuth: true, fotaBlocked: true };
            }
            if (!fota.deviceFoundInFota) {
                return { key, status: 'info', messageKey: 'device_config_not_found', fotaBlocked: true };
            }

            const configDate = fota.lastConfigUpdateDate ?? undefined;
            if (!fota.currentConfigFileName) {
                return {
                    key,
                    status: 'fail',
                    messageKey: 'device_config_no_config_assigned',
                    latestConfigFile: fota.latestConfigFileName ?? undefined,
                    configUpdateDate: configDate,
                    fotaBlocked: true,
                };
            }
            if (fota.isLatest) {
                return {
                    key,
                    status: 'pass',
                    messageRaw: fota.currentConfigFileName,
                    currentConfigFile: fota.currentConfigFileName,
                    latestConfigFile: fota.latestConfigFileName ?? undefined,
                    configUpdateDate: configDate,
                };
            }
            // Check if this vehicle had a non-latest config intentionally applied
            let configOverridden = false;
            try {
                const stored: string[] = JSON.parse(localStorage.getItem('fota_config_override_vehicles') ?? '[]');
                configOverridden = stored.includes(dto.vehicleId);
            } catch { /* ignore */ }

            return {
                key,
                status: 'fail',
                messageKey: configOverridden ? 'device_config_outdated_override' : 'device_config_outdated',
                currentConfigFile: fota.currentConfigFileName,
                latestConfigFile: fota.latestConfigFileName ?? undefined,
                configUpdateDate: configDate,
                fotaBlocked: !configOverridden,
            };
        } catch {
            return { key, status: 'info', messageKey: 'device_config_not_found', fotaBlocked: true };
        }
    }, []);

    const formatVoltage = (mV: number): string => (mV / 1000) + 'V';

    const VOLTAGE_PRESETS = ['13100', '13300', '27100', '13700'];

    const loadCurrentVoltageParam = useCallback(async (deviceId: string) => {
        const TIMEOUT = 30;
        const POLL_INTERVAL = 5;
        setVoltageParamLoading(true);
        setVoltageParamLoaded(null);
        setVoltageParamCountdown(TIMEOUT);
        try {
            const { iccid } = await DeviceChecklistService.sendGetParam(deviceId);
            const startTime = Date.now();
            const countdownInterval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime) / 1000);
                const remaining = Math.max(0, TIMEOUT - elapsed);
                setVoltageParamCountdown(remaining);
            }, 1000);
            try {
                const maxAttempts = Math.floor(TIMEOUT / POLL_INTERVAL);
                for (let i = 0; i < maxAttempts; i++) {
                    await delay(POLL_INTERVAL * 1000);
                    try {
                        const result = await DeviceChecklistService.getParamResponse(iccid);
                        if (result.found && result.value) {
                            if (VOLTAGE_PRESETS.includes(result.value)) {
                                setVoltageCustom(false);
                                setVoltageValue(result.value);
                            } else {
                                setVoltageCustom(true);
                                setVoltageValue(result.value);
                            }
                            setVoltageParamLoaded(true);
                            return;
                        }
                    } catch {
                        // continue polling
                    }
                }
                setVoltageParamLoaded(false);
            } finally {
                clearInterval(countdownInterval);
                setVoltageParamCountdown(0);
            }
        } catch {
            setVoltageParamLoaded(false);
        } finally {
            setVoltageParamLoading(false);
        }
    }, []);

    const runVoltageCheck = useCallback(async (dto: DeviceChecklistDto, date?: Date): Promise<CheckItem> => {
        const key = 'check_voltage_battery';
        if (!dto.deviceCode) {
            return { key, status: 'info', messageKey: 'voltage_no_data' };
        }
        try {
            const dateStr = formatDateForApi(date ?? selectedDate);
            const stats = await DeviceChecklistService.getVoltageStats(dto.deviceCode, dateStr);
            if (!stats || stats.totalItems === 0) {
                return { key, status: 'info', messageKey: 'voltage_no_data' };
            }
            const dateLabel = `${t('deviceChecklist.voltage_since')}: ${formatDate(dateStr)}`;
            const vMin = t('deviceChecklist.voltage_min');
            const vMax = t('deviceChecklist.voltage_max');
            const lines: string[] = [];

            // Stopped section (speed = 0)
            if (stats.minVoltageAtRest != null || stats.maxVoltageAtRest != null) {
                lines.push(t('deviceChecklist.voltage_stopped_title'));
                const parts: string[] = [];
                if (stats.minVoltageAtRest != null) parts.push(`${vMin}: ${formatVoltage(stats.minVoltageAtRest)}`);
                if (stats.maxVoltageAtRest != null) parts.push(`${vMax}: ${formatVoltage(stats.maxVoltageAtRest)}`);
                lines.push(parts.join(' | '));
            }

            // Moving section (speed > 0)
            if (stats.minVoltageMoving != null || stats.maxVoltageMoving != null) {
                if (lines.length > 0) lines.push('');
                lines.push(t('deviceChecklist.voltage_moving_title'));
                const parts: string[] = [];
                if (stats.minVoltageMoving != null) parts.push(`${vMin}: ${formatVoltage(stats.minVoltageMoving)}`);
                if (stats.maxVoltageMoving != null) parts.push(`${vMax}: ${formatVoltage(stats.maxVoltageMoving)}`);
                lines.push(parts.join(' | '));
            }

            lines.push('');
            lines.push(dateLabel);

            return { key, status: 'info', messageRaw: lines.join('\n') };
        } catch {
            return { key, status: 'info', messageKey: 'voltage_no_data' };
        }
    }, [selectedDate, t]);

    const runRemainingChecks = useCallback(async (dto: DeviceChecklistDto, date?: Date) => {
        const fotaKey = 'check_device_configuration';
        const remainingKeys = getCheckKeys(dto).filter(k => k !== fotaKey);
        for (let i = 0; i < remainingKeys.length; i++) {
            if (abortRef.current) break;
            setChecks(prev => [...prev, { key: remainingKeys[i], status: 'loading' }]);
            await delay(STEP_DELAY);
            if (abortRef.current) break;
            const result = remainingKeys[i] === 'check_voltage_battery'
                ? await runVoltageCheck(dto, date)
                : buildCheckResult(remainingKeys[i], dto);
            setChecks(prev => prev.map(c => c.key === remainingKeys[i] ? result : c));
            // If no tracking data, skip the remaining checks since they all depend on it
            if (remainingKeys[i] === 'check_tracking_today' && !dto.filteredTrackingDate) {
                break;
            }
        }
    }, [buildCheckResult, getCheckKeys, runVoltageCheck]);

    const runChecksSequentially = useCallback(async (dto: DeviceChecklistDto, manualDate?: boolean) => {
        abortRef.current = false;
        setRunningChecks(true);
        setChecks([]);

        // ── Phase 1: FOTA check runs first ──
        const fotaKey = 'check_device_configuration';
        setChecks([{ key: fotaKey, status: 'loading' }]);
        await delay(STEP_DELAY);
        if (abortRef.current) { setRunningChecks(false); return; }
        const fotaResult = await runFotaConfigCheck(dto);
        setChecks([fotaResult]);

        // Set the date filter from the FOTA config update date (skip if user manually picked a date)
        let activeDto = dto;
        let activeDate = selectedDate;
        if (!manualDate && fotaResult.configUpdateDate) {
            const fotaDate = new Date(fotaResult.configUpdateDate);
            if (!isNaN(fotaDate.getTime())) {
                activeDate = fotaDate;
                setSelectedDate(fotaDate);
                try {
                    const plate = dto.licensePlate ?? licensePlate.trim();
                    activeDto = await DeviceChecklistService.getChecklistData(plate, formatDateForApi(fotaDate));
                    setData(activeDto);
                } catch {
                    // If re-fetch fails, continue with original data
                }
            }
        }

        // Pre-fill the Km input with the latest known km
        if (activeDto.km > 0) {
            setFotaKmValue(String(Math.round(activeDto.km)));
        } else if (dto.km > 0) {
            setFotaKmValue(String(Math.round(dto.km)));
        }

        // If FOTA blocked (outdated, session expired, device not found) → stop here, show "Continue anyway"
        if (fotaResult.fotaBlocked) {
            setRunningChecks(false);
            return;
        }

        await runRemainingChecks(activeDto, activeDate);
        setRunningChecks(false);
    }, [runRemainingChecks, runFotaConfigCheck, licensePlate]);

    const handleContinueAnyway = useCallback(async () => {
        if (!data) return;
        setRunningChecks(true);
        await runRemainingChecks(data);
        setRunningChecks(false);
    }, [data, runRemainingChecks]);

    const rerunSingleCheck = useCallback(async (key: string) => {
        if (!data) return;
        setChecks(prev => prev.map(c => c.key === key ? { key, status: 'loading' as CheckStatus } : c));
        try {
            const freshData = await DeviceChecklistService.getChecklistData(licensePlate.trim(), formatDateForApi(selectedDate));
            setData(freshData);
            await delay(STEP_DELAY);
            const result = key === 'check_device_configuration'
                ? await runFotaConfigCheck(freshData)
                : key === 'check_voltage_battery'
                    ? await runVoltageCheck(freshData)
                    : buildCheckResult(key, freshData);
            setChecks(prev => prev.map(c => c.key === key ? result : c));
        } catch {
            setChecks(prev => prev.map(c => c.key === key ? { key, status: 'fail' as CheckStatus, messageKey: 'search_error' } : c));
        }
    }, [data, licensePlate, selectedDate, buildCheckResult, runFotaConfigCheck, runVoltageCheck]);

    const closeFotaEdit = useCallback(() => {
        setFotaEditOpen(false);
        setFotaConfigs(null);
        setFotaConfigsError(null);
        setFotaSelectedFileId(null);
    }, []);

    const handleOpenFotaEdit = useCallback(async (currentConfigFile?: string) => {
        if (!data?.deviceModelName) return;
        setFotaEditOpen(true);
        setFotaConfigsError(null);
        setFotaKmValue(data.km > 0 ? String(Math.round(data.km)) : '');
        if (fotaConfigs !== null) return;
        setFotaConfigsLoading(true);
        try {
            const response = await DeviceChecklistService.getFotaConfigFiles(data.deviceModelName);
            if (response.sessionExpired) {
                closeFotaEdit();
                return;
            }
            setFotaConfigs(response.configFiles ?? []);
            setFotaSelectedFileId(response.configFiles?.[0]?.id ?? null);
        } catch {
            setFotaConfigsError(t('deviceChecklist.fota_load_configs_error'));
        } finally {
            setFotaConfigsLoading(false);
        }
    }, [data, fotaConfigs, closeFotaEdit, t]);

    const handleApplyFotaConfig = useCallback(() => {
        if (!data?.deviceCode || !fotaSelectedFileId) return;
        setFotaApplyModalOpen(true);
    }, [data, fotaSelectedFileId]);

    const handleApplyModalClose = useCallback(async (applySuccess: boolean) => {
        setFotaApplyModalOpen(false);
        if (applySuccess && data) {
            // If the applied config is not the latest, remember this vehicle so
            // the checklist won't block on the outdated-config check next time.
            const latestFileId = fotaConfigs?.[0]?.id;
            if (fotaSelectedFileId && latestFileId && fotaSelectedFileId !== latestFileId) {
                try {
                    const stored: string[] = JSON.parse(localStorage.getItem('fota_config_override_vehicles') ?? '[]');
                    if (!stored.includes(data.vehicleId)) {
                        stored.push(data.vehicleId);
                        localStorage.setItem('fota_config_override_vehicles', JSON.stringify(stored));
                    }
                } catch { /* ignore */ }
            }
            closeFotaEdit();
            await runChecksSequentially(data);
        }
    }, [closeFotaEdit, data, fotaConfigs, fotaSelectedFileId, runChecksSequentially]);

    const loadTrackingItems = useCallback(async (vehicleId: string, date: Date, page: number = 1) => {
        if (!vehicleId) return;
        setTrackingLoading(true);
        try {
            const startDate = formatDateForApi(date);
            const result = await DeviceChecklistService.getTrackingItems(vehicleId, startDate, undefined, page, DEFAULT_PAGINATION_ITEMS_PER_PAGE);
            setTrackingPage(result);
            setTrackingCurrentPage(page);
        } catch {
            setTrackingPage(null);
        } finally {
            setTrackingLoading(false);
        }
    }, []);

    const handleToggleTrackingItems = useCallback(() => {
        setTrackingItemsOpen(prev => {
            const willOpen = !prev;
            if (willOpen && data?.vehicleId && !trackingPage) {
                loadTrackingItems(data.vehicleId, selectedDate, 1);
            }
            return willOpen;
        });
    }, [data, selectedDate, trackingPage, loadTrackingItems]);

    const handleTrackingPageChange = useCallback((page: number) => {
        if (data?.vehicleId) {
            loadTrackingItems(data.vehicleId, selectedDate, page);
        }
    }, [data, selectedDate, loadTrackingItems]);

    const handleSearch = async (plateOverride?: string) => {
        const plate = (plateOverride ?? licensePlate).trim();
        if (!plate) return;
        abortRef.current = true;
        setSearching(true);
        setData(null);
        setNotFound(false);
        setChecks([]);
        setKmValue('');
        setFuelTankValue('');
        setTrackingItemsOpen(false);
        setTrackingPage(null);
        setTrackingCurrentPage(1);
        closeFotaEdit();
        try {
            const result = await DeviceChecklistService.getChecklistData(plate, formatDateForApi(selectedDate));
            setData(result);
            setSearching(false);
            if (result.deviceId) {
                runChecksSequentially(result);
            }
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setNotFound(true);
            } else {
                toast.error(t('deviceChecklist.search_error'));
            }
            setSearching(false);
        }
    };

    const handleRetryAll = async () => {
        if (!licensePlate.trim()) return;
        abortRef.current = true;
        setChecks([]);
        try {
            const result = await DeviceChecklistService.getChecklistData(licensePlate.trim(), formatDateForApi(selectedDate));
            setData(result);
            if (result.deviceId) {
                runChecksSequentially(result);
            }
        } catch {
            toast.error(t('deviceChecklist.search_error'));
        }
    };

    const handleSendOdoset = async () => {
        if (!data?.deviceId || !kmValue) return;
        setSendingOdoset(true);
        try {
            await DeviceChecklistService.sendOdoset({ deviceId: data.deviceId, km: parseFloat(kmValue) });
            toast.success(t('deviceChecklist.odoset_sent_success'));
        } catch {
            toast.error(t('deviceChecklist.odoset_sent_error'));
        } finally {
            setSendingOdoset(false);
        }
    };

    const handleSendVoltage = async () => {
        if (!data?.deviceId || !voltageValue) return;
        setSendingVoltage(true);
        try {
            await DeviceChecklistService.sendSetparamVoltage({ deviceId: data.deviceId, voltage: parseInt(voltageValue) });
            toast.success(t('deviceChecklist.voltage_sent_success'));
        } catch {
            toast.error(t('deviceChecklist.voltage_sent_error'));
        } finally {
            setSendingVoltage(false);
        }
    };

    const handleSaveFuelTank = async () => {
        if (!data?.vehicleId || !fuelTankValue) return;
        setSavingFuelTank(true);
        try {
            await DeviceChecklistService.updateFuelTank({ vehicleId: data.vehicleId, fuelTank: parseFloat(fuelTankValue), rowVersion: data.rowVersion });
            toast.success(t('deviceChecklist.fuel_saved_success'));
            await rerunSingleCheck('check_fuel');
        } catch {
            toast.error(t('deviceChecklist.fuel_saved_error'));
        } finally {
            setSavingFuelTank(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { handleSearch(); }
    };

    const selectStyles: StylesConfig<PlateOption, false> = {
        control: (base, state) => ({
            ...base,
            minHeight: '42px',
            backgroundColor: '#f8fafc',
            borderColor: state.isFocused ? '#38bdf8' : '#e2e8f0',
            borderRadius: '0.75rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(56, 189, 248, 0.3)' : 'none',
            fontSize: '0.875rem',
            cursor: 'text',
            '&:hover': { borderColor: state.isFocused ? '#38bdf8' : '#cbd5e1' },
        }),
        valueContainer: (base) => ({ ...base, padding: '0 0.75rem' }),
        placeholder: (base) => ({ ...base, color: '#94a3b8' }),
        singleValue: (base) => ({ ...base, color: '#0f172a', textTransform: 'uppercase' }),
        input: (base) => ({ ...base, color: '#0f172a', textTransform: 'uppercase', margin: 0, padding: 0 }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base) => ({ ...base, padding: '0 0.5rem', color: '#94a3b8' }),
        clearIndicator: (base) => ({ ...base, padding: '0 0.25rem', color: '#94a3b8' }),
        menu: (base) => ({ ...base, fontSize: '0.875rem', borderRadius: '0.75rem', overflow: 'hidden', zIndex: 50 }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#0ea5e9' : state.isFocused ? '#e0f2fe' : 'white',
            color: state.isSelected ? 'white' : '#0f172a',
            cursor: 'pointer',
        }),
    };

    const renderPlateSearchBar = () => {
        const selectedOption: PlateOption | null = licensePlate
            ? (vehicleOptions.find(o => o.value === licensePlate) ?? null)
            : null;

        return (
            <div className="flex min-w-0">
                {optionsLoadError
                    ? (
                    <input
                        type="text"
                        className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:bg-slate-100"
                        placeholder={t('deviceChecklist.search_placeholder')}
                        value={licensePlate}
                        onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                        onKeyDown={handleKeyPress}
                        disabled={searching}
                    />
                    )
                    : (
                    <div className="flex-1 min-w-0">
                        <Select<PlateOption, false>
                            options={vehicleOptions}
                            value={selectedOption}
                            isLoading={optionsLoading}
                            isClearable
                            isSearchable
                            isDisabled={searching}
                            placeholder={t('deviceChecklist.search_placeholder')}
                            noOptionsMessage={() => t('deviceChecklist.autocomplete_no_options')}
                            loadingMessage={() => t('deviceChecklist.autocomplete_loading')}
                            classNamePrefix="plate-select"
                            styles={selectStyles}
                            onChange={(opt) => {
                                const plate = (opt?.value ?? '').toUpperCase();
                                setLicensePlate(plate);
                                if (plate) handleSearch(plate);
                            }}
                            onKeyDown={handleKeyPress}
                            filterOption={(option, raw) => {
                                if (!raw) return true;
                                return option.label.toLowerCase().includes(raw.toLowerCase());
                            }}
                        />
                    </div>
                )}
                {searching && (
                    <div className="shrink-0 flex items-center px-3">
                        <Spinner />
                    </div>
                )}
            </div>
        );
    };

    /* ── Status helpers ── */

    const renderIndicator = (status: CheckStatus) => {
        const base = 'w-7 h-7 rounded-full shrink-0 flex items-center justify-center';
        switch (status) {
            case 'pass': return <div className={`${base} bg-emerald-100 text-emerald-600`}><FontAwesomeIcon icon={faCheck} className="text-xs" /></div>;
            case 'fail': return <div className={`${base} bg-red-100 text-red-500`}><FontAwesomeIcon icon={faTimes} className="text-xs" /></div>;
            case 'info': return <div className={`${base} bg-amber-100 text-amber-500`}><FontAwesomeIcon icon={faInfoCircle} className="text-xs" /></div>;
            case 'loading': return <div className={`${base} bg-sky-100 text-sky-500`}><Spinner /></div>;
            default: return null;
        }
    };

    const renderBadge = (status: CheckStatus) => {
        const base = 'inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-0.5';
        switch (status) {
            case 'pass': return <span className={`${base} bg-emerald-100 text-emerald-700`}>{t('deviceChecklist.status_pass')}</span>;
            case 'fail': return <span className={`${base} bg-red-100 text-red-600`}>{t('deviceChecklist.status_fail')}</span>;
            case 'info': return <span className={`${base} bg-amber-100 text-amber-700`}>{t('deviceChecklist.status_info')}</span>;
            case 'loading': return <span className={`${base} bg-sky-100 text-sky-600`}>...</span>;
            default: return null;
        }
    };

    const okCount = checks.filter(c => c.status === 'pass').length;
    const failCount = checks.filter(c => c.status === 'fail').length;
    const progressPct = checks.length > 0 ? Math.round((okCount / checks.length) * 100) : 0;

    const showResults = data || notFound;

    /* ── Km/Fuel input block ── */
    const renderKmInput = (isOdoset: boolean) => (
        <div className="flex flex-col gap-2 mt-3">
            <input
                type="number"
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-slate-50 placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 transition-all"
                placeholder={isOdoset ? t('deviceChecklist.km_input_label') : t('deviceChecklist.fuel_input_label')}
                value={isOdoset ? kmValue : fuelTankValue}
                onChange={(e) => isOdoset ? setKmValue(e.target.value) : setFuelTankValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        if (isOdoset) { handleSendOdoset(); } else { handleSaveFuelTank(); }
                    }
                }}
                min={0}
            />
            <button
                className="flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all w-full border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={isOdoset ? handleSendOdoset : handleSaveFuelTank}
                disabled={isOdoset ? (sendingOdoset || !kmValue) : (savingFuelTank || !fuelTankValue)}
            >
                {(isOdoset ? sendingOdoset : savingFuelTank)
                    ? <Spinner />
                    : <><FontAwesomeIcon icon={faPaperPlane} className="text-xs" /> {t('deviceChecklist.send_odoset')}</>
                }
            </button>
        </div>
    );

    return (
        <ScreenTitle title={t('deviceChecklist.title')}>
            <>
            <div className="max-w-2xl mx-auto px-2 sm:px-4 py-3 sm:py-5">

                {/* Search Section - centered when no results */}
                {!showResults && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <div className="w-full max-w-[500px]">
                            <div className="text-center mb-4">
                                <FontAwesomeIcon icon={faClipboardCheck} size="3x" className="text-sky-500 mb-3" />
                                <h4 className="text-xl font-semibold">{t('deviceChecklist.title')}</h4>
                            </div>
                            {renderPlateSearchBar()}
                        </div>
                    </div>
                )}

                {/* Results */}
                {showResults && (
                    <div className="space-y-4">
                        {/* Title */}
                        <div>
                            <h1 className="text-lg font-bold text-slate-900">{t('deviceChecklist.title')}</h1>
                            <p className="text-sm text-slate-500 mt-0.5">{t('deviceChecklist.search_placeholder')}</p>
                        </div>

                        {/* Search bar */}
                        {renderPlateSearchBar()}

                        {notFound && (
                            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-sm">{t('deviceChecklist.no_vehicle_found')}</div>
                        )}

                        {data && (<>
                            {/* ── Vehicle Info Card ── */}
                            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                                {/* Dark header */}
                                <div className="bg-slate-900 px-4 pt-4 pb-5">
                                    <div className="flex items-start gap-3">
                                        {/* License plate */}
                                        <div className="shrink-0 bg-white rounded-lg px-3 py-2 border-b-4 border-slate-300 text-center min-w-[90px]">
                                            <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">PT</p>
                                            <p className="text-base font-black text-slate-900 font-mono tracking-widest leading-none m-0">
                                                {data.licensePlate}
                                            </p>
                                        </div>
                                        {/* Name */}
                                        <div className="flex-1 min-w-0 pt-1">
                                            <p className="text-white font-bold text-base leading-tight m-0">{data.designation || data.licensePlate}</p>
                                        </div>
                                    </div>
                                </div>
                                {/* Fields */}
                                <div>
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('deviceChecklist.device_model')}</p>
                                        <p className="text-sm font-medium text-slate-800 m-0">{data.deviceModelName || '-'}</p>
                                    </div>
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('deviceChecklist.sim_card')}</p>
                                        <p className="text-sm font-medium text-slate-800 font-mono break-all leading-snug m-0">{data.simCardCode || '-'}</p>
                                    </div>
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('deviceChecklist.fuel_tank_capacity')}</p>
                                        <p className="text-sm font-medium text-slate-800 m-0">{data.fuelTank > 0 ? `${data.fuelTank} L` : '-'}</p>
                                    </div>
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('deviceChecklist.device_code')}</p>
                                        <p className="text-sm font-medium text-slate-800 font-mono break-all leading-snug m-0">{data.deviceCode || '-'}</p>
                                    </div>
                                    <div className="px-4 py-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">{t('deviceChecklist.last_tracking')}</p>
                                        <p className="text-sm font-medium text-slate-800 m-0">{formatDate(data.lastTrackingDate)}</p>
                                    </div>
                                    <div className="px-4 py-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">{t('deviceChecklist.has_identification_tag')}</p>
                                        {data.hasIdentificationTag
                                            ? (
                                                <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-3 py-1 text-xs font-semibold">
                                                    <FontAwesomeIcon icon={faCheck} className="text-[10px]" /> {t('common.yes')}
                                                </span>
                                            )
                                            : (
                                                <span className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-600 rounded-full px-3 py-1 text-xs font-semibold">
                                                    <FontAwesomeIcon icon={faTimes} className="text-[10px]" /> {t('common.no')}
                                                </span>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>

                            {/* No device warning */}
                            {!data.deviceId && (
                                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">{t('deviceChecklist.no_device_assigned')}</div>
                            )}

                            {/* ── Checklist Card ── */}
                            {data.deviceId && checks.length > 0 && (
                                <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                                    {/* Header */}
                                    <div className="px-4 py-4 space-y-3" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                                                <FontAwesomeIcon icon={faClipboardCheck} className="text-sm" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-slate-800 leading-tight m-0">{t('deviceChecklist.checklist')}</p>
                                                <p className="text-[11px] text-slate-400 mt-0.5 m-0">{checks.length} {t('deviceChecklist.checks_count') || 'checks'}</p>
                                            </div>
                                            <div className="shrink-0 hidden sm:flex items-center gap-2">
                                                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('deviceChecklist.after')}</span>
                                                <DateTimePicker
                                                    selected={selectedDate}
                                                    onChange={(d: Date | null) => {
                                                        if (!d) return;
                                                        setSelectedDate(d);
                                                        if (licensePlate.trim()) {
                                                            void (async () => {
                                                                abortRef.current = true;
                                                                setChecks([]);
                                                                try {
                                                                    const result = await DeviceChecklistService.getChecklistData(licensePlate.trim(), formatDateForApi(d));
                                                                    setData(result);
                                                                    if (result.deviceId) {
                                                                        runChecksSequentially(result, true);
                                                                    }
                                                                } catch {
                                                                    toast.error(t('deviceChecklist.search_error'));
                                                                }
                                                            })();
                                                        }
                                                    }}
                                                    showTimeSelect
                                                    timeFormat="HH:mm"
                                                    timeIntervals={15}
                                                    dateFormat="dd/MM/yyyy HH:mm"
                                                    maxDate={new Date()}
                                                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400"
                                                    disabled={runningChecks}
                                                />
                                            </div>
                                            <button
                                                className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 shrink-0 border border-slate-200 rounded-xl px-3 py-2 hover:border-sky-400 hover:text-sky-500 hover:bg-sky-50 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={handleRetryAll}
                                                disabled={runningChecks}
                                            >
                                                <FontAwesomeIcon icon={faRotateRight} className={`text-xs ${runningChecks ? 'animate-spin' : ''}`} />
                                                <span className="hidden sm:inline">{t('deviceChecklist.retry')}</span>
                                            </button>
                                        </div>
                                        {/* Date filter — mobile only (below the header row) */}
                                        <div className="flex sm:hidden items-center gap-2">
                                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0">{t('deviceChecklist.after')}</span>
                                            <DateTimePicker
                                                selected={selectedDate}
                                                onChange={(d: Date | null) => {
                                                    if (!d) return;
                                                    setSelectedDate(d);
                                                    if (licensePlate.trim()) {
                                                        void (async () => {
                                                            abortRef.current = true;
                                                            setChecks([]);
                                                            try {
                                                                const result = await DeviceChecklistService.getChecklistData(licensePlate.trim(), formatDateForApi(d));
                                                                setData(result);
                                                                if (result.deviceId) {
                                                                    runChecksSequentially(result, true);
                                                                }
                                                            } catch {
                                                                toast.error(t('deviceChecklist.search_error'));
                                                            }
                                                        })();
                                                    }
                                                }}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                maxDate={new Date()}
                                                className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400"
                                                disabled={runningChecks}
                                            />
                                        </div>

                                        {/* Progress bar */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full transition-all duration-700 ease-out"
                                                    style={{ width: `${progressPct}%`, background: 'linear-gradient(to right, #38bdf8, #34d399)' }}
                                                />
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                                                    {okCount} OK
                                                </span>
                                                <span className="text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                                    {failCount} {t('deviceChecklist.status_fail')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Check rows */}
                                    {checks.map((check) => (
                                        <div key={check.key} className="flex gap-3 px-4 py-4 hover:bg-slate-50/60 transition-colors" style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            {/* Left indicator */}
                                            <div style={{ marginTop: 2 }}>
                                                {renderIndicator(check.status)}
                                            </div>
                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-sm font-semibold text-slate-800 leading-snug m-0">{t(`deviceChecklist.${check.key}`)}</p>
                                                    {check.status !== 'loading' && !runningChecks && (
                                                        <button
                                                            onClick={() => rerunSingleCheck(check.key)}
                                                            title={t('deviceChecklist.retry')}
                                                            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:border-sky-400 hover:text-sky-500 hover:bg-sky-50 active:scale-95 transition-all"
                                                        >
                                                            <FontAwesomeIcon icon={faRotateRight} className="text-[11px]" />
                                                        </button>
                                                    )}
                                                </div>
                                                {check.status !== 'loading' && (check.messageKey || check.messageRaw) && (
                                                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed m-0 whitespace-pre-line">
                                                        {check.messageKey && check.messageRaw
                                                            ? `${t(`deviceChecklist.${check.messageKey}`)} (${check.messageRaw})`
                                                            : check.messageRaw
                                                                ?? (check.messageSuffix
                                                                    ? `${t(`deviceChecklist.${check.messageKey}`)}: ${check.messageSuffix}`
                                                                    : t(`deviceChecklist.${check.messageKey}`))}
                                                    </p>
                                                )}
                                                <div className="mt-2">
                                                    {renderBadge(check.status)}
                                                </div>

                                                {/* FOTA not authenticated — contact administrator */}
                                                {check.key === 'check_device_configuration' && check.requiresFotaAuth && check.status !== 'loading' && (
                                                    <p className="text-xs text-amber-600 mt-2 m-0">{t('deviceChecklist.fota_contact_admin')}</p>
                                                )}

                                                {/* Device configuration filenames */}
                                                {check.key === 'check_device_configuration' && check.status !== 'loading' && (check.currentConfigFile || check.latestConfigFile || !check.requiresFotaAuth) && (
                                                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-2.5">
                                                        {/* Edit button — shown even without current config */}
                                                        {isFotaAdmin && (check.status === 'pass' || check.status === 'fail') && !fotaEditOpen && (
                                                            <div className="flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleOpenFotaEdit(check.currentConfigFile)}
                                                                    title={t('deviceChecklist.fota_edit_config')}
                                                                    className="shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-500 hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 active:scale-95 transition-all bg-white"
                                                                >
                                                                    <FontAwesomeIcon icon={faPenToSquare} className="text-[10px]" />
                                                                    <span>{t('deviceChecklist.fota_edit_config')}</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                        {check.currentConfigFile && (
                                                            <div>
                                                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1 m-0">
                                                                    {t('deviceChecklist.device_config_current')}
                                                                </p>
                                                                <p className="text-sm font-mono font-semibold text-slate-800 break-all leading-snug m-0">
                                                                    {check.currentConfigFile}
                                                                </p>
                                                                {check.configUpdateDate && (
                                                                    <p className="text-[11px] text-slate-500 mt-0.5 m-0">
                                                                        {t('deviceChecklist.device_config_last_update')}: {formatDate(check.configUpdateDate)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {check.latestConfigFile && check.latestConfigFile !== check.currentConfigFile && (
                                                            <div>
                                                                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1 m-0">
                                                                    {t('deviceChecklist.device_config_latest')}
                                                                </p>
                                                                <p className="text-sm font-mono font-semibold text-emerald-700 break-all leading-snug m-0">
                                                                    {check.latestConfigFile}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Edit configuration panel — admin only */}
                                                {isFotaAdmin && check.key === 'check_device_configuration' && fotaEditOpen && (
                                                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                                                        <div className="px-4 pt-4 pb-3 border-b border-slate-100 flex items-center gap-3">
                                                            <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-500 flex items-center justify-center shrink-0">
                                                                <FontAwesomeIcon icon={faPenToSquare} className="text-sm" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-semibold text-slate-800 leading-tight m-0">
                                                                    {t('deviceChecklist.fota_edit_config')}
                                                                </p>
                                                                <p className="text-[11px] text-slate-400 mt-0.5 m-0">
                                                                    {t('deviceChecklist.fota_select_config')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="px-4 py-4 space-y-3">
                                                            <div>
                                                                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
                                                                    {t('deviceChecklist.fota_apply_km_label')}
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    value={fotaKmValue}
                                                                    onChange={e => setFotaKmValue(e.target.value)}
                                                                    disabled={fotaApplying}
                                                                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:opacity-60"
                                                                />
                                                            </div>
                                                            {fotaConfigsLoading && (
                                                                <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                                                                    <Spinner /> {t('deviceChecklist.fota_load_configs_loading')}
                                                                </div>
                                                            )}
                                                            {!fotaConfigsLoading && fotaConfigsError && (
                                                                <div className="bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2">{fotaConfigsError}</div>
                                                            )}
                                                            {!fotaConfigsLoading && !fotaConfigsError && fotaConfigs && fotaConfigs.length === 0 && (
                                                                <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-xl px-3 py-2">
                                                                    {t('deviceChecklist.fota_no_configs_for_model')}
                                                                </div>
                                                            )}
                                                            {!fotaConfigsLoading && !fotaConfigsError && fotaConfigs && fotaConfigs.length > 0 && (
                                                                <select
                                                                    value={fotaSelectedFileId ?? ''}
                                                                    onChange={(e) => setFotaSelectedFileId(Number(e.target.value))}
                                                                    disabled={fotaApplying}
                                                                    className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 disabled:opacity-60 font-mono"
                                                                >
                                                                    {fotaConfigs.map(f => (
                                                                        <option key={f.id} value={f.id}>
                                                                            {f.name}{f.createdAt ? ` — ${formatDate(f.createdAt)}` : ''}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            <div className="flex gap-2 pt-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={closeFotaEdit}
                                                                    disabled={fotaApplying}
                                                                    className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-50 bg-white"
                                                                >
                                                                    {t('deviceChecklist.fota_cancel_edit')}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleApplyFotaConfig}
                                                                    disabled={fotaApplying || !fotaSelectedFileId || fotaConfigsLoading}
                                                                    className="flex-1 flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 border-0 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {fotaApplying ? <Spinner /> : t('deviceChecklist.fota_apply_config')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Last identification tag read */}
                                                {check.key === 'check_identification_tag' && check.status !== 'loading' && check.lastTagCode && (
                                                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1 m-0">
                                                            {t('deviceChecklist.last_identification_tag_read')}
                                                        </p>
                                                        <p className="text-sm font-mono font-semibold text-slate-800 break-all leading-snug m-0">
                                                            {check.lastTagCode}
                                                        </p>
                                                        <p className="text-[11px] text-slate-500 mt-0.5 m-0">
                                                            {formatDate(check.lastTagDate)}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Km input */}
                                                {check.key === 'check_km' && check.status !== 'loading' && !data.hasCanReading && (
                                                    renderKmInput(true)
                                                )}

                                                {/* Fuel input */}
                                                {check.key === 'check_fuel' && check.status === 'fail' && data.hasCanReading && (
                                                    renderKmInput(false)
                                                )}

                                                {/* Voltage select + send */}
                                                {check.key === 'check_voltage_battery' && check.status !== 'loading' && data.deviceId && (
                                                    <div className="flex flex-col gap-2 mt-3">
                                                        {/* Load current value from device */}
                                                        {voltageParamLoading && (
                                                            <div className="flex items-center gap-2 text-xs text-slate-500 py-1">
                                                                <Spinner className="w-3 h-3" /> {t('deviceChecklist.voltage_param_loading')} ({voltageParamCountdown}s)
                                                            </div>
                                                        )}
                                                        {!voltageParamLoading && voltageParamLoaded === true && (
                                                            <p className="text-xs text-emerald-600 m-0 py-1">
                                                                {t('deviceChecklist.voltage_param_loaded')} ({voltageValue})
                                                            </p>
                                                        )}
                                                        {!voltageParamLoading && voltageParamLoaded === false && (
                                                            <p className="text-xs text-amber-600 m-0 py-1">
                                                                {t('deviceChecklist.voltage_param_failed')}
                                                            </p>
                                                        )}
                                                        {!voltageParamLoading && voltageParamLoaded === null && (
                                                            <button
                                                                className="flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-100 active:scale-95 text-slate-600 text-sm font-semibold rounded-xl px-4 py-2 transition-all w-full bg-white"
                                                                onClick={() => data.deviceId && loadCurrentVoltageParam(data.deviceId)}
                                                            >
                                                                <FontAwesomeIcon icon={faSearch} className="text-xs" /> {t('deviceChecklist.voltage_load_from_device')}
                                                            </button>
                                                        )}
                                                        <select
                                                            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-slate-50 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 transition-all disabled:opacity-60"
                                                            value={voltageCustom ? 'custom' : voltageValue}
                                                            disabled={voltageParamLoading}
                                                            onChange={(e) => {
                                                                if (e.target.value === 'custom') {
                                                                    setVoltageCustom(true);
                                                                    setVoltageValue('');
                                                                } else {
                                                                    setVoltageCustom(false);
                                                                    setVoltageValue(e.target.value);
                                                                }
                                                            }}
                                                        >
                                                            <option value="13100">{t('deviceChecklist.voltage_option_combustion')} (13100)</option>
                                                            <option value="13300">{t('deviceChecklist.voltage_option_some_cars')} (13300)</option>
                                                            <option value="27100">{t('deviceChecklist.voltage_option_buses_24v')} (27100)</option>
                                                            <option value="13700">{t('deviceChecklist.voltage_option_electric')} (13700)</option>
                                                            <option value="custom">{t('deviceChecklist.voltage_option_custom')}</option>
                                                        </select>
                                                        {voltageCustom && (
                                                            <input
                                                                type="number"
                                                                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 bg-slate-50 placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400/30 focus:border-sky-400 transition-all"
                                                                placeholder={t('deviceChecklist.voltage_input_label')}
                                                                value={voltageValue}
                                                                onChange={(e) => setVoltageValue(e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') handleSendVoltage(); }}
                                                                min={0}
                                                            />
                                                        )}
                                                        <button
                                                            className="flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 active:scale-95 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all w-full border-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            onClick={handleSendVoltage}
                                                            disabled={sendingVoltage || voltageParamLoading || !voltageValue}
                                                        >
                                                            {sendingVoltage
                                                                ? <Spinner />
                                                                : <><FontAwesomeIcon icon={faPaperPlane} className="text-xs" /> {t('deviceChecklist.send_voltage')}</>
                                                            }
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Continue anyway — after FOTA blocked, at the bottom of the checklist */}
                            {!runningChecks && checks.length === 1 && checks[0]?.fotaBlocked && checks[0]?.status !== 'loading' && (
                                <div className="flex justify-center mt-4">
                                    <button
                                        onClick={handleContinueAnyway}
                                        className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 active:scale-95 text-slate-600 text-xs font-semibold rounded-xl px-4 py-2.5 bg-white transition-all"
                                    >
                                        {t('deviceChecklist.fota_continue_anyway')}
                                    </button>
                                </div>
                            )}

                        </>)}

                        <div className="pb-1" />
                    </div>
                )}
            </div>

            {/* ── Tracking Items — collapsible, animated width ── */}
            {data && data.vehicleId && (
                <div
                    className="mx-auto px-2 sm:px-4 pb-10"
                    style={{
                        maxWidth: trackingItemsOpen ? '100%' : '672px',
                        transition: 'max-width 0.4s ease-in-out',
                    }}
                >
                    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                        <button
                            type="button"
                            onClick={handleToggleTrackingItems}
                            className="w-full px-4 py-4 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors border-0 cursor-pointer"
                        >
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                                    <FontAwesomeIcon icon={faClipboardCheck} className="text-sm" />
                                </div>
                                <p className="text-sm font-semibold text-slate-800 leading-tight m-0">{t('deviceChecklist.tracking_items_section')}</p>
                                {trackingItemsOpen && data?.vehicleId && (
                                    <span
                                        role="button"
                                        onClick={(e) => { e.stopPropagation(); loadTrackingItems(data.vehicleId, selectedDate, trackingCurrentPage); }}
                                        className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 cursor-pointer transition-colors"
                                        title={t('common.refresh')}
                                    >
                                        <FontAwesomeIcon icon={faRotateRight} className={`text-xs ${trackingLoading ? 'animate-spin' : ''}`} />
                                    </span>
                                )}
                            </div>
                            <FontAwesomeIcon icon={trackingItemsOpen ? faChevronUp : faChevronDown} className="text-xs text-slate-400" />
                        </button>

                        {trackingItemsOpen && (
                            <div style={{ borderTop: '1px solid #e2e8f0' }}>
                                {trackingLoading && (
                                    <div className="flex items-center justify-center py-8">
                                        <Spinner />
                                    </div>
                                )}

                                {!trackingLoading && (!trackingPage || trackingPage.totalItems === 0) && (
                                    <div className="text-center text-slate-400 text-sm py-8">
                                        {t('tracking_items.no_data')}
                                    </div>
                                )}

                                {!trackingLoading && trackingPage && trackingPage.totalItems > 0 && (
                                    <TrackingItemsTable
                                        trackingPage={trackingPage}
                                        currentPage={trackingCurrentPage}
                                        onPageChange={handleTrackingPageChange}
                                        style={{ fontSize: '0.8rem' }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FotaApplyModal
                open={fotaApplyModalOpen && !!data && !!fotaSelectedFileId}
                imei={data?.deviceCode ?? ''}
                fileId={fotaSelectedFileId ?? 0}
                deviceId={data?.deviceId ?? ''}
                simCardCode={data?.simCardCode ?? ''}
                km={parseFloat(fotaKmValue) || 0}
                deviceModelName={data?.deviceModelName ?? ''}
                onClose={handleApplyModalClose}
            />
            </>
        </ScreenTitle>
    );
};

export default DeviceChecklistScreen;
